
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import {
  registerEventFromApp,
  listenFromParent,
  sendToParent,
  ParentEvDataInitRes,
  ParentEvDataOpenRes,
  platform,
  Bridge,
} from '@/common/bridge';
import { onOpenMyRequest, onOpenArticle, onFloatButton, emitFloatButton } from '@/common/eventBus';
import MyRequestComponent from '@/components/MyRequest/index.vue';
import Article from '@/components/Article.vue';
import FloatButton from '@/components/FloatButton.vue';
import ConsentGA from '@/components/ConsentGA.vue';
import { CustomerServiceProvider, MINIMIZE_TIME } from '@/common/config';
import { LoginResult, SelectedEntity } from './common/types';
import { ConnectToAgent } from './common/bot/types';
import { store } from './store';
import { GA_EVENT_NAME_ENUM, GA_SHOW_CONSENT_BANNER } from './common/gaHelper';
import { bootstrap, setOptions } from 'vue-gtag';
import * as env from '@/common/environment';
import { analytics } from './common/logs/EventLogger';
import { GENERAL_EVENTS } from './common/logs/events';
import { getEntryLog, OpenHelpEntryType } from './common/entryLog';
import { logErrorEvents } from './common/api/api.pleased';

const UserModule = namespace('user');
const analyticsConsentModule = namespace('analyticsConsent');

@Component({
  components: {
    MyRequestComponent,
    Article,
    FloatButton,
    ConsentGA,
  },
})
export default class App extends Vue {
  @UserModule.State('pageSource') pageSource!: OpenHelpEntryType;
  @UserModule.Mutation('initProject') initProject!: Function;
  @UserModule.Action('sportyLoginSuccess') sportyLoginSuccess!: Function;
  @UserModule.Mutation('updateSelectedEntity') updateSelectedEntity!: (payload: SelectedEntity | null) => void;

  @analyticsConsentModule.Getter('showConsentBanner') showConsentBanner!: boolean;

  showFloatButton = false;
  showMyRequest = false;
  fetchArticle = false;
  isFromAutocomplete = false;
  articleTitle = '';
  categoryName = '';
  showArticle = false;
  showArticleId: string|number = '';
  articleBody = '';
  articleSectionTitle = '';
  hasInit = false;

  articleUseConfig: null | ConnectToAgent = null;

  created () {
    // Interaction
    registerEventFromApp(); // Need to only call once before listenFromParent, it only used for App(handled internally)

    // Listen
    const onLogin = (data: LoginResult) => {
      if (Object.prototype.hasOwnProperty.call(data, 'bizCode')) {
        if (data.bizCode === 10000) {
          this.sportyLoginSuccess(data);
        }
      } else {
        this.sportyLoginSuccess(data);
      }
    };

    Bridge.onLoginFromPopup(onLogin);
    Bridge.onLoginSuccess(onLogin);

    listenFromParent<ParentEvDataOpenRes>('OPEN', async data => {
      // Currently we don't receive OPEN event on chatbot reopening, this is flow is to be revised
      // Due to that, we don't reset selected entity on chatbot minimize/go back, which results in undesired (showing prev flow)
      this.updateSelectedEntity(data.selectedEntity);
      emitFloatButton(false);
      sendToParent('MINIMIZE', {
        period: MINIMIZE_TIME, // sec
        open: true,
      });

      const chatStore = (store.state as { chat: { isInitialized: boolean }}).chat;

      // If already initialized, then we need to send to redirect page
      if (chatStore.isInitialized) {
        Bridge.redirectToLastEventPage();
      }
    });
    listenFromParent<ParentEvDataInitRes>('INIT', async data => {
      const { open } = data;
      if (!this.hasInit && !open) { // For INIT event like Sportybet web in home page, should also do minimize
        emitFloatButton(true);
      }
      this.initProject({
        ...data,
        provider: CustomerServiceProvider.PLEASED,
      });

      if ((platform === 'wap' || platform === 'web')) {
        if (open) { // For INIT event like Sportybet web in home page, only when open Chatroom/index.vue when it is opened, to reduce API call (ex: /user/createUser, /category/list)
          this.hasInit = true;
        }
      } else { // natvie
        this.hasInit = true;
      }
    });
    // listenFromParent('GO_LIVE_CHAT', (data) => {
    //   this.$router.push({ name: 'chatroom' });
    // });
    sendToParent('INIT'); // Bridge-INIT-Send

    // For dev test, uncomment all lines until onFloatButton. You can also use demo.html to serve it
    // this.initProject({
    //   isLogin: true,
    //   country: 'ng',
    //   token: 'patron:id:accesstoken:bab68ff240d9bace0be120250f0f3313FTjHqTwkiAve3zRwD+aM02zWltPDD9hlbgKdla52sk5DuQuKJxUOg9fLaTlonkgkTlCNQmkdFvAvXi+inL9u5Q==',
    //   chatStatus: '',
    //   provider: CustomerServiceProvider.PLEASED,
    //   phone: '',
    //   email: '',
    // }); // 7771110000 test1234ng
    // this.hasInit = true;

    onFloatButton(isOpen => {
      this.showFloatButton = (isOpen && (platform === 'wap' || platform === 'web')); // Only mobile web and desktop can use FloatButton
      if (isOpen === false) {
        this.hasInit = true;

        const chatStore = (store.state as { chat: { isInitialized: boolean }}).chat;

        // If already initialized, then we need to send to redirect page
        if (chatStore.isInitialized) {
          Bridge.redirectToLastEventPage();
        }
      }
    });

    onOpenMyRequest(isOpen => {
      this.showMyRequest = isOpen;
    });
    onOpenArticle(data => {
      this.showArticle = data.isOpen;
      this.showArticleId = data.articleId || '';
      this.articleTitle = data.title || '';
      this.categoryName = data.categoryName || '';
      this.articleBody = data.body || '';
      this.articleSectionTitle = data.sectionTitle || '';
      this.fetchArticle = data.fetchArticle || false;
      this.isFromAutocomplete = data.isFromAutocomplete || false;
      this.articleUseConfig = data.useConfig || null;
    });

    window.addEventListener('error', (error) => {
      logErrorEvents({ event: 'Window Error', message: error.message });
    });

    document.addEventListener('error', (error) => {
      logErrorEvents({ event: 'Document Error', message: error.message });
    }, true);
  }

  @Watch('hasInit')
  onOpenedChatbotChange () {
    if (this.hasInit) {
      if (!GA_SHOW_CONSENT_BANNER) {
        const gaId = env.get('gaId');

        if (gaId) {
          setOptions({
            config: {
              id: gaId,
              params: {
                anonymize_ip: true,
                brand: env.get('brandCode'),
                country: env.get('country'),
                platform,
              },
            },
          });

          bootstrap().then((_gtag) => {
            // gtag is undefined here
            this.$gtag.event(GA_EVENT_NAME_ENUM.OPEN_CHATBOT);
          });
        }

        const entryLog = getEntryLog(this.pageSource);

        analytics.startFlushingLogs();
        analytics.track({
          name: GENERAL_EVENTS.CHATBOT_OPENED,
          metadata: {
            source: entryLog.entryType,
            url: entryLog.url,
          },
        });
      }
    }
  }
}
